import { useCallback, useEffect, useRef, useState } from "react";
import { apiGet, type ApiResponse } from "@/lib/api";
import type { LatLon, MapBounds, MapMarker } from "@/lib/types";

// 대전광역시청 (기본 중심)
export const DEFAULT_CENTER: LatLon = { lat: 36.3511, lon: 127.385 };
export const DEFAULT_LEVEL = 6;

interface UseBizmapParams {
  cate: string;
  coords: LatLon | null;
  bounds: MapBounds | null;
}

interface BizmapState {
  markers: MapMarker[];
  jsKey: string;
  center: LatLon;
  level: number;
  total: number;
  loading: boolean;
  error: string | null;
  fetched: boolean;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function extractMarkers(data: unknown): MapMarker[] {
  if (Array.isArray(data)) return data as MapMarker[];
  const rec = asRecord(data);
  if (rec) {
    if (Array.isArray(rec.markers)) return rec.markers as MapMarker[];
    if (Array.isArray(rec.list)) return rec.list as MapMarker[];
  }
  return [];
}

function extractJsKey(res: ApiResponse<unknown>): string {
  if (typeof res.js_key === "string" && res.js_key) return res.js_key;
  const rec = asRecord(res.data);
  const k = rec?.js_key;
  return typeof k === "string" ? k : "";
}

function extractCenter(res: ApiResponse<unknown>, fallback: LatLon): LatLon {
  const rec = asRecord(res.data);
  // map.php 는 center:{lat,lon,level} 객체를 내려줍니다.
  const centerObj = asRecord(res.center) ?? asRecord(rec?.center);
  const lat = Number(
    centerObj?.lat ?? res.center_lat ?? rec?.center_lat ?? rec?.lat
  );
  const lon = Number(
    centerObj?.lon ?? res.center_lon ?? rec?.center_lon ?? rec?.lon
  );
  if (Number.isFinite(lat) && Number.isFinite(lon) && lat && lon) {
    return { lat, lon };
  }
  return fallback;
}

function extractLevel(res: ApiResponse<unknown>, fallback: number): number {
  const rec = asRecord(res.data);
  const centerObj = asRecord(res.center) ?? asRecord(rec?.center);
  const level = Number(centerObj?.level ?? res.center_level ?? rec?.level);
  return Number.isFinite(level) && level > 0 ? level : fallback;
}

function buildUrl({ cate, coords, bounds }: UseBizmapParams): string {
  const p = new URLSearchParams();
  if (cate) p.set("cate", cate);
  if (coords) {
    p.set("lat", String(coords.lat));
    p.set("lon", String(coords.lon));
  }
  if (bounds) {
    p.set("sw_lat", String(bounds.sw_lat));
    p.set("sw_lon", String(bounds.sw_lon));
    p.set("ne_lat", String(bounds.ne_lat));
    p.set("ne_lon", String(bounds.ne_lon));
  }
  const qs = p.toString();
  return `map.php${qs ? `?${qs}` : ""}`;
}

export function useBizmap(params: UseBizmapParams) {
  const url = buildUrl(params);
  const jsKeyRef = useRef("");
  const centerRef = useRef<LatLon>(DEFAULT_CENTER);
  const levelRef = useRef<number>(DEFAULT_LEVEL);
  const [tick, setTick] = useState(0);

  const [state, setState] = useState<BizmapState>({
    markers: [],
    jsKey: "",
    center: DEFAULT_CENTER,
    level: DEFAULT_LEVEL,
    total: 0,
    loading: true,
    error: null,
    fetched: false,
  });

  useEffect(() => {
    let active = true;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    apiGet<unknown>(url)
      .then((res) => {
        if (!active) return;
        const markers = extractMarkers(res.data);
        const jsKey = extractJsKey(res) || jsKeyRef.current;
        const center = extractCenter(res, centerRef.current);
        const level = extractLevel(res, levelRef.current);
        jsKeyRef.current = jsKey;
        centerRef.current = center;
        levelRef.current = level;
        setState({
          markers,
          jsKey,
          center,
          level,
          total: typeof res.total === "number" ? res.total : markers.length,
          loading: false,
          error: null,
          fetched: true,
        });
      })
      .catch((e: Error) => {
        if (!active) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          error: e.message || "지도 정보를 불러오지 못했습니다.",
          fetched: true,
        }));
      });

    return () => {
      active = false;
    };
  }, [url, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  return { ...state, reload };
}